/* eslint-disable no-useless-escape */

import {
  ActionPerformed,
  PushNotificationSchema,
  PushNotifications,
  Token,
} from "@capacitor/push-notifications";
import { Capacitor } from "@capacitor/core";
import { registerDevice } from "../services/notifications.service";
import { ENV } from "../environments/environment";
import { registerSW } from 'virtual:pwa-register';

const WEB_PUSH_INSTALLATION_ID_STORAGE_KEY = "resonance_web_push_installation_id";

const getWebNotificationPermission = (): NotificationPermission | "unsupported" => {
  if (typeof Notification === "undefined") {
    return "unsupported";
  }

  return Notification.permission;
};

const createInstallationId = (): string => {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }

  return `${Date.now()}-${Math.random().toString(16).slice(2)}${Math.random().toString(16).slice(2)}`;
};

const getOrCreateWebPushInstallationId = (): string => {
  const generatedId = createInstallationId();

  if (typeof window === "undefined" || !window.localStorage) {
    return generatedId;
  }

  try {
    const existingId = window.localStorage.getItem(WEB_PUSH_INSTALLATION_ID_STORAGE_KEY);
    if (existingId) {
      return existingId;
    }

    window.localStorage.setItem(WEB_PUSH_INSTALLATION_ID_STORAGE_KEY, generatedId);
    return generatedId;
  } catch (_error) {
    return generatedId;
  }
};

// Utility: Converts base64 public key to Uint8Array
export const urlB64ToUint8Array = (base64String: string): Uint8Array => {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
};

// ✅ Register Web Notifications using PWA's injected service worker
const registerWebNotifications = async ({ studyId, public_key }: { studyId: number, public_key: string }) => {
  // Wait for the service worker to be ready
  const registration = await navigator.serviceWorker.ready;

  if (!registration) {
    console.warn("Service worker registration not available.");
    return;
  }

  const publicKeyUsed = public_key || ENV.VAPID_PUBLIC_KEY;

  // Check for existing subscription before subscribing again
  let subscription = await registration.pushManager.getSubscription();
  if (!subscription) {
    subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlB64ToUint8Array(publicKeyUsed) as BufferSource,
    });
  }

  const installationId = getOrCreateWebPushInstallationId();

  await registerDevice(
    { studyId },
    {
      type: "WEB",
      installationId,
      deviceCredentials: subscription,
    }
  );
};


// ✅ Register Mobile Push Notifications
const registerMobileNotification = async ({ studyId }: { studyId: number }) => {
  const register = () => {
    PushNotifications.register();

    PushNotifications.addListener("registration", (token: Token) => {
      console.log("Push registration success");
      registerDevice(
        { studyId },
        { type: "MOBILE", deviceCredentials: { token: token.value } }
      );
    });

    PushNotifications.addListener("registrationError", (error: any) => {
      console.log("Error on registration: " + JSON.stringify(error));
    });

    PushNotifications.addListener("pushNotificationReceived", (notification: PushNotificationSchema) => {
      console.log("Push received", notification);
    });

    PushNotifications.addListener("pushNotificationActionPerformed", (action: ActionPerformed) => {
      console.log("Push tapped", action);
      // Navigate to the notification target. The backend sends { slug, url } in the FCM
      // data payload; AppUrlListener (mounted in the router) performs the client-side
      // navigation so we stay inside the SPA router on native.
      const data = (action.notification?.data ?? {}) as { slug?: string; url?: string };
      const target = data.url || (data.slug ? `/${data.slug}` : "/");
      if (typeof window !== "undefined") {
        window.dispatchEvent(new CustomEvent("push-navigate", { detail: { target } }));
      }
    });

    PushNotifications.createChannel({
      id: '1',
      name: 'Study Notifications',
      description: 'Study notifications',
      importance: 4,
      visibility: 1,
      lights: true,
      vibration: true,
    });
  };

  let currentPermission = await PushNotifications.checkPermissions();
  if (currentPermission.receive !== 'granted') {
    currentPermission = await PushNotifications.requestPermissions();
  }

  if (currentPermission.receive === 'granted') {
    register();
  }
};

const unregisterMobileNotifications = () => {
  // Implement logic to unregister mobile push notifications if needed
};

// ✅ Unregister Web Notifications (optional cleanup)
const unregisterWebNotification = async () => {
  try {
    const registration = await navigator.serviceWorker.getRegistration();
    if (registration) await registration.unregister();
  } catch (error) {
    console.warn("Cannot get service worker to unregister", error);
  }
};

// ✅ Entry Point
export const registerNotifications = async ({ studyId, public_key }: { studyId: number, public_key: string }) => {
  if (Capacitor.isNativePlatform()) {
    await registerMobileNotification({ studyId });
    return unregisterMobileNotifications;
  } else {
    await registerWebNotifications({ studyId, public_key });
    return unregisterWebNotification;
  }
};

export const ensurePushNotificationsEnabled = async ({
  studyId,
  public_key,
}: {
  studyId: number;
  public_key: string;
}): Promise<boolean> => {
  try {
    if (!Capacitor.isNativePlatform()) {
      const currentPermission = getWebNotificationPermission();

      if (currentPermission === "denied") {
        return false;
      }

      if (currentPermission === "default") {
        const requestedPermission = await Notification.requestPermission();
        if (requestedPermission !== "granted") {
          return false;
        }
      }
    }

    await registerNotifications({ studyId, public_key });

    if (Capacitor.isNativePlatform()) {
      const permission = await PushNotifications.checkPermissions();
      return permission.receive === "granted";
    }

    return getWebNotificationPermission() === "granted";
  } catch (error) {
    console.warn("Failed to enable push notifications", error);
    return false;
  }
};
